import {
  ArrowLeft,
  Award,
  Calendar,
  Camera,
  DollarSign,
  MapPin,
  Star,
  ThumbsDown,
  ThumbsUp,
  User,
  Video
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to construct proper image URLs
const getImageUrl = (relativeUrl) => {
  if (!relativeUrl) return '';
  if (relativeUrl.startsWith('http')) return relativeUrl;
  
  // Get the base server URL without /api
  const serverBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
  return `${serverBaseUrl}${relativeUrl}`;
};

const RateVendor = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingRating, setExistingRating] = useState(null);
  
  // Rating form state
  const [overallRating, setOverallRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [categoryRatings, setCategoryRatings] = useState({
    quality: 0,
    timeliness: 0,
    professionalism: 0,
    communication: 0,
    cleanliness: 0
  });
  const [categoryHoverRatings, setCategoryHoverRatings] = useState({});
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [positiveAspects, setPositiveAspects] = useState([]);
  const [negativeAspects, setNegativeAspects] = useState([]);
  const [images, setImages] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);

  const ratingLabels = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Very Good',
    5: 'Excellent'
  };

  const categoryLabels = {
    quality: 'Work Quality',
    timeliness: 'Timeliness',
    professionalism: 'Professionalism',
    communication: 'Communication',
    cleanliness: 'Cleanliness'
  };

  const positiveOptions = [
    { value: 'ON_TIME', label: 'On Time' },
    { value: 'PROFESSIONAL', label: 'Professional' },
    { value: 'GOOD_QUALITY', label: 'Good Quality' },
    { value: 'FAIR_PRICING', label: 'Fair Pricing' },
    { value: 'CLEAN_WORK', label: 'Clean Work' },
    { value: 'GOOD_COMMUNICATION', label: 'Good Communication' },
    { value: 'PROBLEM_SOLVER', label: 'Problem Solver' },
    { value: 'COURTEOUS', label: 'Courteous' },
    { value: 'WELL_EQUIPPED', label: 'Well Equipped' },
    { value: 'KNOWLEDGEABLE', label: 'Knowledgeable' }
  ];

  const negativeOptions = [
    { value: 'LATE', label: 'Late' },
    { value: 'UNPROFESSIONAL', label: 'Unprofessional' },
    { value: 'POOR_QUALITY', label: 'Poor Quality' },
    { value: 'OVERPRICED', label: 'Overpriced' },
    { value: 'MESSY_WORK', label: 'Messy Work' },
    { value: 'POOR_COMMUNICATION', label: 'Poor Communication' },
    { value: 'INCOMPLETE_WORK', label: 'Incomplete Work' },
    { value: 'RUDE_BEHAVIOR', label: 'Rude Behavior' },
    { value: 'LACK_OF_TOOLS', label: 'Lack of Tools' },
    { value: 'INEXPERIENCED', label: 'Inexperienced' }
  ];

  const fetchJobDetails = useCallback(async () => {
    // Validate user and role
    if (!user || user.role !== 'customer') {
      console.warn('Invalid user context for rating');
      navigate('/login', { replace: true });
      return;
    }

    // Validate jobId before making API calls
    if (!jobId || jobId === 'undefined' || jobId === 'null') {
      console.warn('Invalid jobId provided:', jobId);
      navigate('/feedback', { replace: true });
      return;
    }

    try {
      setLoading(true);
      
      // Fetch job details
      const jobResponse = await api.jobs.getById(jobId);
      const jobData = jobResponse.job;
      
      if (!jobData) {
        console.warn('Job not found for ID:', jobId);
        toast.error('Job not found');
        navigate('/feedback', { replace: true });
        return;
      }

      // More flexible customer ID comparison
      const jobCustomerId = jobData.customerId?._id || jobData.customerId;
      const currentUserId = user._id || user.id; // Handle both _id and id formats
      
      if (jobCustomerId?.toString() !== currentUserId?.toString()) {
        console.warn('Access denied: Job does not belong to current user');
        navigate('/feedback', { replace: true });
        return;
      }

      if (jobData.status !== 'COMPLETED') {
        toast.error('Job must be completed to submit a rating');
        navigate(`/jobs/${jobId}`);
        return;
      }

      setJob(jobData);

      // Check if rating already exists
      try {
        const ratingResponse = await api.jobs.getRating(jobId);
        if (ratingResponse.rating) {
          setExistingRating(ratingResponse.rating);
          // Pre-fill form with existing rating
          const rating = ratingResponse.rating;
          setOverallRating(rating.overallRating);
          if (rating.criteria) {
            setCategoryRatings({
              quality: rating.criteria.quality || 0,
              timeliness: rating.criteria.timeliness || 0,
              professionalism: rating.criteria.professionalism || 0,
              communication: rating.criteria.communication || 0,
              cleanliness: rating.criteria.cleanliness || 0
            });
          }
          setTitle(rating.title || '');
          setComment(rating.comment || '');
          setWouldRecommend(rating.wouldRecommend);
          setPositiveAspects(rating.positiveAspects || []);
          setNegativeAspects(rating.negativeAspects || []);
          setIsAnonymous(rating.isAnonymous || false);
        }
      } catch (ratingError) {
        // No existing rating found, that's fine
        console.log('No existing rating found');
      }

    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  }, [jobId, user, navigate]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const handleCategoryRating = (category, rating) => {
    setCategoryRatings(prev => ({
      ...prev,
      [category]: rating
    }));
  };

  const handleCategoryHover = (category, rating) => {
    setCategoryHoverRatings(prev => ({
      ...prev,
      [category]: rating
    }));
  };

  const calculateOverallFromCategories = () => {
    const ratings = Object.values(categoryRatings);
    const validRatings = ratings.filter(r => r > 0);
    if (validRatings.length === 0) return 0;
    return Math.round(validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length);
  };

  // Auto-update overall rating when category ratings change
  useEffect(() => {
    const calculatedOverall = calculateOverallFromCategories();
    if (calculatedOverall > 0 && overallRating === 0) {
      setOverallRating(calculatedOverall);
    }
  }, [categoryRatings]);

  const handleAspectToggle = (aspect, isPositive) => {
    if (isPositive) {
      setPositiveAspects(prev => 
        prev.includes(aspect) 
          ? prev.filter(a => a !== aspect)
          : [...prev, aspect]
      );
    } else {
      setNegativeAspects(prev => 
        prev.includes(aspect) 
          ? prev.filter(a => a !== aspect)
          : [...prev, aspect]
      );
    }
  };

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files);

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB limit

      if (!isImage && !isVideo) {
        toast.error(`${file.name} is not a valid image or video file`);
        return false;
      }

      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum size is 50MB`);
        return false;
      }

      return true;
    });

    if (validFiles.length + images.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    if (validFiles.length === 0) return;

    try {
      // Create temporary preview items with loading state
      const tempMedia = validFiles.map(file => ({
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        type: 'GENERAL',
        isUploading: true,
        mediaType: file.type.startsWith('image/') ? 'image' : 'video'
      }));

      setImages(prev => [...prev, ...tempMedia]);

      // Upload files to server
      const uploadResponse = await api.jobs.uploadRatingImages(validFiles.map(file => ({ file })));

      if (uploadResponse.images) {
        // Replace temporary items with uploaded items
        setImages(prev => {
          const newImages = [...prev];
          // Remove the temporary items
          const tempCount = tempMedia.length;
          newImages.splice(-tempCount, tempCount);

          // Add the uploaded items
          const uploadedMedia = uploadResponse.images.map(item => ({
            url: getImageUrl(item.url),
            name: item.originalName,
            type: item.type || 'GENERAL',
            isUploading: false,
            mediaType: item.mediaType || (item.originalName?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/) ? 'video' : 'image')
          }));

          return [...newImages, ...uploadedMedia];
        });

        // Cleanup blob URLs
        tempMedia.forEach(item => URL.revokeObjectURL(item.url));

        const imageCount = validFiles.filter(f => f.type.startsWith('image/')).length;
        const videoCount = validFiles.filter(f => f.type.startsWith('video/')).length;

        let message = '';
        if (imageCount > 0 && videoCount > 0) {
          message = `${imageCount} image(s) and ${videoCount} video(s) uploaded successfully`;
        } else if (imageCount > 0) {
          message = `${imageCount} image(s) uploaded successfully`;
        } else {
          message = `${videoCount} video(s) uploaded successfully`;
        }

        toast.success(message);
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload files. Please try again.');

      // Remove temporary items on error
      setImages(prev => prev.filter(img => !img.isUploading));

      // Cleanup blob URLs
      validFiles.forEach(file => {
        const url = URL.createObjectURL(file);
        URL.revokeObjectURL(url);
      });
    }
  };

  const removeImage = (index) => {
    setImages(prev => {
      const updated = [...prev];
      const imageToRemove = updated[index];
      
      // Only revoke blob URLs, not server URLs
      if (imageToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageToRemove.url);
      }
      
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasOverallRating = overallRating > 0;
    const hasCategoryRatings = Object.values(categoryRatings).some(r => r > 0);

    if (!hasOverallRating && !hasCategoryRatings) {
      toast.error('Please provide either an overall rating or detailed category ratings');
      return;
    }

    if (wouldRecommend === null) {
      toast.error('Please indicate if you would recommend this vendor');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please provide some comments about your experience');
      return;
    }

    setSubmitting(true);
    
    try {
      const ratingData = {
        overallRating,
        criteria: categoryRatings,
        title: title.trim(),
        comment: comment.trim(),
        wouldRecommend,
        positiveAspects,
        negativeAspects,
        images: images.filter(img => !img.isUploading).map(img => ({
          url: img.url,
          type: img.type || 'GENERAL'
        })),
        isAnonymous
      };

      await api.jobs.submitRating(jobId, ratingData);
      
      toast.success('Rating submitted successfully!');
      navigate('/jobs', { state: { message: 'Thank you for your feedback!' } });
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStarRating = (value, onChange, size = 'w-8 h-8', category = null) => (
    <div className="flex space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange && onChange(star)}
          onMouseEnter={() => {
            if (category) {
              handleCategoryHover(category, star);
            } else {
              setHoverRating(star);
            }
          }}
          onMouseLeave={() => {
            if (category) {
              handleCategoryHover(category, 0);
            } else {
              setHoverRating(0);
            }
          }}
          className="focus:outline-none transition-transform hover:scale-110"
          disabled={!onChange}
        >
          <Star
            className={`${size} transition-colors ${
              star <= (category ? (categoryHoverRatings[category] || value) : (hoverRating || value))
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="bg-white rounded-lg p-6 space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h1>
        <button
          onClick={() => navigate('/jobs')}
          className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700"
        >
          Back to Jobs
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-0" style={{ backgroundColor: '#F9FAFB' }}>
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-orange-100 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
            <div className="flex items-center space-x-3">
              <Star className="w-6 h-6 text-orange-100" />
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {existingRating ? 'Update Rating' : 'Rate Your Experience'}
                </h1>
                <p className="text-orange-100">Share your feedback to help other customers</p>
              </div>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Job Summary */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{job.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2 text-orange-600" />
                  <span>{job.vendorId.firstName} {job.vendorId.lastName}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-orange-600" />
                  <span>{new Date(job.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-orange-600" />
                  <span>{job.location?.city}</span>
                </div>
                {job.totalAmount && (
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 mr-2 text-orange-600" />
                    <span>${job.totalAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
            <span className="bg-green-50 text-green-700 border border-green-200 px-4 py-2 rounded-full text-sm font-medium">
              Completed
            </span>
          </div>
        </div>

        {/* Rating Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-8">
          {/* Overall Rating */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Star className="w-5 h-5 mr-2 text-orange-600" />
              Overall Rating *
            </label>
            <div className="flex items-center space-x-4">
              {renderStarRating(overallRating, setOverallRating, 'w-10 h-10')}
              {overallRating > 0 && (
                <span className="text-lg font-medium text-orange-600">
                  {ratingLabels[overallRating]}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              You can rate overall or use detailed ratings below - they work independently
            </p>
          </div>

          {/* Category Ratings */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-4">
              Detailed Performance Ratings (Optional)
            </label>
            <div className="space-y-4 bg-orange-50 p-6 rounded-lg border border-orange-100">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  {categoryRatings[key] > 0 && (
                    <span className="ml-2 text-sm text-gray-600">
                      ({ratingLabels[categoryRatings[key]]})
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {renderStarRating(
                    categoryRatings[key],
                    (rating) => handleCategoryRating(key, rating),
                    'w-6 h-6',
                    key
                  )}
                </div>
              </div>
            ))}
              <div className="text-xs text-gray-500 mt-4">
                Rate each aspect individually. Your overall rating will be calculated automatically if not set manually.
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-2">
              Review Title (Optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your experience"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
              maxLength={100}
            />
          </div>

          {/* Comment */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-2">
              Your Review *
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell others about your experience with this vendor..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
              maxLength={1000}
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              {comment.length}/1000 characters
            </p>
          </div>

          {/* Recommendation */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-4">
              Would you recommend this vendor? *
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setWouldRecommend(true)}
                className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg border-2 transition-all ${
                  wouldRecommend === true
                    ? 'border-green-500 bg-green-50 text-green-700 font-semibold'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ThumbsUp className="w-5 h-5 mr-2" />
                Yes, I recommend
              </button>
              <button
                type="button"
                onClick={() => setWouldRecommend(false)}
                className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg border-2 transition-all ${
                  wouldRecommend === false
                    ? 'border-red-500 bg-red-50 text-red-700 font-semibold'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ThumbsDown className="w-5 h-5 mr-2" />
                No, I don't recommend
              </button>
            </div>
          </div>

          {/* Positive Aspects */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-4">
              What did they do well? (Optional)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {positiveOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleAspectToggle(option.value, true)}
                  className={`p-3 text-sm rounded-lg border-2 transition-all font-medium ${
                    positiveAspects.includes(option.value)
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Negative Aspects */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-4">
              What could be improved? (Optional)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {negativeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleAspectToggle(option.value, false)}
                  className={`p-3 text-sm rounded-lg border-2 transition-all font-medium ${
                    negativeAspects.includes(option.value)
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Photos */}
          <div>
            <label className="block text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Camera className="w-5 h-5 mr-2 text-orange-600" />
              Add Photos or Videos (Optional)
            </label>
            <div className="border-2 border-dashed border-orange-300 rounded-lg p-8 text-center bg-orange-50 hover:bg-orange-100 transition-colors">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleMediaUpload}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className="cursor-pointer text-orange-600 hover:text-orange-700"
              >
                <Camera className="w-10 h-10 mx-auto mb-3 text-orange-500" />
                <span className="block text-base font-medium">Click to upload photos or videos</span>
                <span className="block text-sm text-gray-500 mt-1">Maximum 5 files, up to 50MB each</span>
              </label>
            </div>

            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4">
                {images.map((media, index) => (
                  <div key={index} className="relative">
                    {media.mediaType === 'video' ? (
                      <div className="relative">
                        <video
                          src={media.url}
                          className={`w-full h-28 object-cover rounded-xl border-2 border-gray-200 ${
                            media.isUploading ? 'opacity-50' : ''
                          }`}
                          controls={!media.isUploading}
                          muted
                          preload="metadata"
                        />
                        <div className="absolute top-2 right-2 bg-black bg-opacity-75 rounded px-2 py-1">
                          <Video className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={media.url}
                        alt={`Upload ${index + 1}`}
                        className={`w-full h-28 object-cover rounded-xl border-2 border-gray-200 ${
                          media.isUploading ? 'opacity-50' : ''
                        }`}
                      />
                    )}
                    {media.isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-xl">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      disabled={media.isUploading}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      ×
                    </button>
                    {/* File name tooltip */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white text-xs p-2 rounded-b-xl truncate">
                      {media.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Privacy Options */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="anonymous"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
              />
              <label htmlFor="anonymous" className="text-gray-700 font-medium">
                Submit this review anonymously
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2 ml-8">
              Your name will not be displayed with this review
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                submitting ||
                (overallRating === 0 && !Object.values(categoryRatings).some(r => r > 0)) ||
                wouldRecommend === null ||
                !comment.trim()
              }
              className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Award className="w-5 h-5 mr-2" />
                  {existingRating ? 'Update Rating' : 'Submit Rating'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RateVendor;
